import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  LocationOn,
  People,
  CalendarToday,
  CheckCircle,
  PauseCircleOutline,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

const CourseCard = ({
  course,
  enrollment,
  isSuspendedEnrollment,
  onEnroll,
  validMembership = true,
  canEnrollInCourse,
  buttonLabel = 'Iscriviti',
}) => {
  const membershipRequired = course.isMembershipRequired && !validMembership;
  const canEnroll = canEnrollInCourse(course);
  let buttonText = buttonLabel;
  if (!canEnroll) {
    if (membershipRequired) {
      buttonText = 'Richiede tessera';
    } else if (course.minLevel >= 6) {
      buttonText = 'Richiesto oltre il 5° anno';
    } else {
      buttonText = `Richiesto ${course.minLevel}° anno`;
    }
  }

  return (
  <Card
    sx={{
      flexGrow: 1,
      height: '100%',
      maxWidth: 360,
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <CardContent sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Typography variant='h6' component='h2'>
          {course.name}
        </Typography>
        {enrollment && (
          <Chip
            icon={
              isSuspendedEnrollment ? <PauseCircleOutline /> : <CheckCircle />
            }
            label={isSuspendedEnrollment ? 'Sospeso' : 'Iscritto'}
            color={isSuspendedEnrollment ? 'warning' : 'success'}
            size='small'
          />
        )}
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {course.description}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
        <Typography variant='body2' color='text.secondary'>
          {course.venue}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <People sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
        <Typography variant='body2' color='text.secondary'>
          {course.remainingSeats || course.availableSeats} posti disponibili
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          <b>Docente:</b>{' '}
          {course.teacher
            ? `${course.teacher.name} ${course.teacher.surname}`
            : 'Da assegnare'}
        </Typography>
      </Box>
      {course.weekdays && course.weekdays.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant='body2' color='text.secondary'>
            <b>Giorni:</b>{' '}
            {course.weekdays
              .map((day) => {
                const dayMap = {
                  0: 'Dom',
                  1: 'Lun',
                  2: 'Mar',
                  3: 'Mer',
                  4: 'Gio',
                  5: 'Ven',
                  6: 'Sab',
                };
                return dayMap[day] || day;
              })
              .join(', ')}{' '}
            alle {course.time}
          </Typography>
        </Box>
      )}
      {course.startDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant='body2' color='text.secondary'>
            <b>Date:</b>{' '}
            {dayjs(course.startDate).locale('it').format('DD-MMM-YY')} -{' '}
            {dayjs(course.endDate).locale('it').format('DD-MMM-YY')}
          </Typography>
        </Box>
      )}
      {(course.pricing.monthly > 0 ||
        course.pricing.annual3 > 0 ||
        course.pricing.annual1 > 0) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            <b>Iscrizione:</b>
            <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
              {course.pricing.monthly > 0 && (
                <Typography variant='body2' color='text.secondary'>
                  {course.pricing.monthly}€ al mese
                </Typography>
              )}
              {course.pricing.annual3 > 0 && (
                <Typography variant='body2' color='text.secondary'>
                  {course.pricing.annual3}€ annuale (3 rate)
                </Typography>
              )}
              {course.pricing.annual1 > 0 && (
                <Typography variant='body2' color='text.secondary'>
                  {course.pricing.annual1}€ annuale (1 rata)
                </Typography>
              )}
            </Box>
          </Typography>
        </Box>
      )}
    </CardContent>
    <CardActions sx={{ p: 2, pt: 0 }}>
      {!enrollment && (
        <Button
          fullWidth
          size='medium'
          variant='contained'
          onClick={() => onEnroll(course)}
          disabled={
            !canEnroll ||
            (course.remainingSeats || course.availableSeats) <= 0 ||
            membershipRequired
          }
          sx={{
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          {buttonText}
        </Button>
      )}
    </CardActions>
  </Card>
  );
};

export default CourseCard;

CourseCard.propTypes = {
  course: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    venue: PropTypes.string,
    remainingSeats: PropTypes.number,
    availableSeats: PropTypes.number,
    teacher: PropTypes.shape({
      name: PropTypes.string,
      surname: PropTypes.string,
    }),
    weekdays: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    time: PropTypes.string,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    pricing: PropTypes.shape({
      monthly: PropTypes.number,
      annual3: PropTypes.number,
      annual1: PropTypes.number,
    }).isRequired,
    isMembershipRequired: PropTypes.bool,
    minLevel: PropTypes.number,
  }).isRequired,
  enrollment: PropTypes.object,
  isSuspendedEnrollment: PropTypes.bool,
  onEnroll: PropTypes.func.isRequired,
  validMembership: PropTypes.bool,
  canEnrollInCourse: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
};

